import TitleBar from '../../../components/TitleBar';
import { Footer } from '../../../components/common/Footer';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  FETCH_PROCESSES_AREAS_BY_LOCATION,
  CREATE_PROCESS_AREA,
  UPDATE_PROCESS_AREA,
  DELETE_PROCESS_AREA,
} from '../queries';

import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';
import { ColumnsType } from 'antd/es/table';
import { tableSortingMethods } from '../../../utils';
import { Button } from '../../../components';
import FinancialData from '../../../components/common/FinancialData';
import { formFields } from './formFields';
import { templateColumnConfigs } from './templateUploadColumns';
import { baseColumns } from './exportColumns';
import { useUserContext } from '../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../components/error-handling';
import TitleUpdater from '../../../components/common/TitleUpdater';

type ProcessArea = {
  processArea: string;
  replacementTime: string;
  replacementCost: string;
  underlyingProcesses: number;
  uuid: string;
};

type ProcessAreaForForm = Omit<ProcessArea, 'underlyingProcesses'>;

const ProcessAreas = () => {
  const params = useParams();
  let [searchParams] = useSearchParams();

  const locationName = searchParams.get('locationName') ?? '';

  const navigate = useNavigate();

  const { canViewFinancials } = useUserContext();

  const { data, loading, error } = useQuery(FETCH_PROCESSES_AREAS_BY_LOCATION, {
    variables: { uuid: params.locationId },
  });

  const [createGQL] = useMutation(CREATE_PROCESS_AREA, {
    refetchQueries: ['FindAllProcessAreasByLocation'],
  });

  const [updateGQL] = useMutation(UPDATE_PROCESS_AREA, {
    refetchQueries: ['FindAllProcessAreasByLocation'],
  });

  const [deleteGQL] = useMutation(DELETE_PROCESS_AREA, {
    refetchQueries: ['FindAllProcessAreasByLocation'],
  });

  const columns: ColumnsType<any> = [
    {
      title: 'Process Area',
      key: 'processArea',
      dataIndex: 'processArea',
      align: 'left',
      width: 200,
      sorter: (a, b) =>
        tableSortingMethods.string(a.processArea, b.processArea),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: (
        <div>
          Replacement Time
          <br />
          (months)
        </div>
      ),
      key: 'replacementTime',
      dataIndex: 'replacementTime',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.replacementTime, b.replacementTime),
      render: (text: number) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Replacement Cost',
      key: 'replacementCost',
      dataIndex: 'replacementCost',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(a.replacementCost, b.replacementCost)
        : undefined,
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} forceShow />}
        </span>
      ),
    },
    {
      title: 'Underlying Processes',
      key: 'underlyingProcesses',
      dataIndex: 'underlyingProcesses',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.underlyingProcesses,
          b.underlyingProcesses,
        ),
      render: (text: number) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
  ];

  const extendedActions = (record: any) => (
    <Button
      type="primary-rounded"
      className="text-sm"
      onClick={() =>
        navigate(
          `/processes/${record.uuid}?processAreaName=${
            record.processArea
          }&locationId=${params.locationId}&locationName=${searchParams.get(
            'locationName',
          )}`,
        )
      }
    >
      Processes
    </Button>
  );

  const processAreasFormatter = (processArea: any) => {
    const replacementCost = parseFloat(processArea.replacementCost?.toString());
    const replacementTime = parseInt(processArea.replacementTime?.toString());

    return {
      ...processArea,
      replacementCost,
      replacementTime,
      location: { uuid: params.locationId },
    };
  };

  const modifyVariables = ({ values }: ModifyVariablesArgs) => {
    const processArea = processAreasFormatter(values);
    return processArea;
  };

  const templateUploadConfig = {
    duplicateKey: 'processArea',
    itemLabel: 'Process Area',
    sampleItemIdentifier: 'Sample Process Area',
    formatter: processAreasFormatter,
  };

  return (
    <>
      <TitleUpdater title={`${locationName}/Process Areas`} />
      <TitleBar
        title="Data Inputs"
        subtitle={`${locationName} // Process Areas`}
        brief="Add process areas below. Doing so will support more granular exposure quantifications and allow you to build mitigation strategies for specific areas within locations."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Process Areas"
          columns={columns}
          data={data?.findAll}
          loading={loading}
          editable
          formFields={formFields}
          createGQL={createGQL}
          updateGQL={updateGQL}
          deleteGQL={deleteGQL}
          modifyVariables={modifyVariables}
          extendActions={extendedActions}
          inlineEditing
          templateColumns={templateColumnConfigs}
          templateUploadConfig={templateUploadConfig}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={`/locations`} />
    </>
  );
};

export default ProcessAreas;
